import { apiFetch, ApiClientError } from "./client";
import { runOidcFlow, startOidcRedirect, type OidcAuthorizationData, OidcError } from "@/lib/oidc";

export class AuthError extends Error {
  constructor(
    public readonly kind:
      | "invalid_credentials"
      | "user_inactive"
      | "user_not_found"
      | "method_not_allowed"
      | "popup_blocked"
      | "state_mismatch"
      | "user_closed"
      | "provider_error"
      | "network"
      | "unknown",
    message: string,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

function b64Utf8(s: string): string {
  // btoa handles only Latin-1. Encode UTF-8 first for usernames with cyrillic etc.
  return btoa(unescape(encodeURIComponent(s)));
}

interface EspoUserResponse {
  user: { id: string; name: string; userName: string; type: string };
  /** Espo session token — must be echoed via Espo-Authorization on subsequent requests. */
  token: string;
}

async function callAppUser(headers: Record<string, string>): Promise<EspoUserResponse> {
  const r = await fetch("/api/v1/App/user", {
    method: "GET",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...headers,
    },
    credentials: "include",
  });

  if (r.status === 401) {
    throw new AuthError("invalid_credentials", "Невірний логін або пароль.");
  }
  if (r.status === 403) {
    throw new AuthError("method_not_allowed", "Цей спосіб входу заборонений для вашого облікового запису.");
  }
  if (!r.ok) {
    let detail = r.headers.get("X-Status-Reason") ?? r.statusText;
    throw new AuthError("unknown", detail || `Сервер повернув ${r.status}.`);
  }

  return (await r.json()) as EspoUserResponse;
}

export function fetchOidcAuthorizationData(): Promise<OidcAuthorizationData> {
  return apiFetch<OidcAuthorizationData>("/Oidc/authorizationData");
}

export async function loginWithPassword(username: string, password: string): Promise<EspoUserResponse> {
  const auth = b64Utf8(`${username}:${password}`);
  // NOTE: deliberately do NOT send Espo-Authorization-Create-Token-Secret.
  // When that header is set, Espo writes a secret to auth_token and REQUIRES
  // the `auth-token-secret` cookie on every subsequent request. Brave/Safari
  // strict-privacy modes drop the Set-Cookie → cookie absent → 401 loop.
  // Token-only auth (no secret) is the portable path across all browsers.
  return callAppUser({
    Authorization: `Basic ${auth}`,
    "Espo-Authorization": auth,
    "Espo-Authorization-By-Token": "false",
  });
}

export async function loginWithOidc(): Promise<EspoUserResponse> {
  let data: OidcAuthorizationData;
  try {
    data = await fetchOidcAuthorizationData();
  } catch (e) {
    if (e instanceof ApiClientError) {
      throw new AuthError("network", `Не вдалося отримати дані OIDC: ${e.message}`);
    }
    throw new AuthError("network", "Не вдалося отримати дані OIDC.");
  }

  let result;
  try {
    result = await runOidcFlow(data);
  } catch (e) {
    if (e instanceof OidcError) {
      switch (e.kind) {
        case "popup_blocked":
          throw new AuthError("popup_blocked", e.message);
        case "state_mismatch":
          throw new AuthError("state_mismatch", e.message);
        case "user_closed":
          throw new AuthError("user_closed", e.message);
        case "provider_error":
          throw new AuthError("provider_error", e.message);
        default:
          throw new AuthError("unknown", e.message);
      }
    }
    throw new AuthError("unknown", "Невідома помилка під час входу через Google.");
  }

  return completeOidcCode(result.code, result.nonce);
}

/**
 * Complete OIDC sign-in from an authorization code (shared by the desktop popup
 * path above and the mobile full-page redirect return below). Reuses the verified
 * §3 contract: GET /api/v1/App/user with Espo-Authorization = base64('**oidc:'+code)
 * + X-Oidc-Authorization-Nonce. Token-only (no Create-Token-Secret) — see
 * loginWithPassword for the Brave/Safari secret-cookie rationale.
 */
function completeOidcCode(code: string, nonce: string): Promise<EspoUserResponse> {
  const auth = b64Utf8(`**oidc:${code}`);
  return callAppUser({
    Authorization: `Basic ${auth}`,
    "Espo-Authorization": auth,
    "X-Oidc-Authorization-Nonce": nonce,
  });
}

/**
 * LOGIN-1 (mobile): start the full-page OIDC redirect. Fetches authorization data,
 * then navigates the whole tab to Google (does not return — the page unloads). On
 * return the custom oauth-callback EntryPoint forwards `?code=&state=` (or `?error=`)
 * to /login, where completeOidcRedirect finishes sign-in. Throws AuthError if the
 * authorization data can't be fetched or sessionStorage is unavailable — the caller
 * surfaces it and the password form stays usable.
 */
export async function startOidcRedirectFlow(): Promise<void> {
  let data: OidcAuthorizationData;
  try {
    data = await fetchOidcAuthorizationData();
  } catch (e) {
    if (e instanceof ApiClientError) {
      throw new AuthError("network", `Не вдалося отримати дані OIDC: ${e.message}`);
    }
    throw new AuthError("network", "Не вдалося отримати дані OIDC.");
  }
  try {
    startOidcRedirect(data);
  } catch {
    throw new AuthError(
      "unknown",
      "Не вдалося почати вхід через Google (сховище недоступне). Скористайтесь паролем або іншим браузером.",
    );
  }
}

/**
 * LOGIN-1 (mobile): complete sign-in from a `?code=&state=` callback the mobile
 * redirect landed on. The CSRF state-check (URL state == persisted state) is done by
 * the caller (LoginPage) before invoking this; here we only exchange code+nonce.
 */
export function completeOidcRedirect(code: string, nonce: string): Promise<EspoUserResponse> {
  return completeOidcCode(code, nonce);
}

export async function logout(): Promise<void> {
  // Hit Espo's logout endpoint (revokes server-side token). Best-effort —
  // client state is reset by the caller regardless.
  try {
    await apiFetch("/App/destroyAuthToken", { method: "POST" });
  } catch {
    // ignore
  }
}
